import { useState } from 'react'
import { ArrowDown, ArrowUp, Copy, FileQuestion, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { cn } from '@/lib/utils'
import type { FieldType, FormField, FormPage } from '../../types'
import { FIELD_TYPES, TYPE_LABEL } from './model'
import { TYPE_ICON } from './typeMeta'

interface Props {
    page: FormPage
    selectedIndex: number | null
    problemKeys: Set<string>
    onSelect: (index: number) => void
    onAdd: (type: FieldType) => void
    onDuplicate: (index: number) => void
    onRemove: (index: number) => void
    onMove: (index: number, delta: number) => void
}

function AddFieldMenu({ onAdd, label = 'Add field' }: { onAdd: (t: FieldType) => void; label?: string }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button size="sm"><Plus data-icon="inline-start" /> {label}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuGroup>
                    <DropdownMenuLabel>Field type</DropdownMenuLabel>
                    {FIELD_TYPES.map((t) => {
                        const Icon = TYPE_ICON[t]
                        return (
                            <DropdownMenuItem key={t} onSelect={() => onAdd(t)}>
                                <Icon /> {TYPE_LABEL[t]}
                            </DropdownMenuItem>
                        )
                    })}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

function FieldCard({ f, i, last, selected, hasProblem, onSelect, onDuplicate, onRemove, onMove }: {
    f: FormField; i: number; last: boolean; selected: boolean; hasProblem: boolean
    onSelect: () => void; onDuplicate: () => void; onRemove: () => void; onMove: (d: number) => void
}) {
    const Icon = TYPE_ICON[f.type]
    const name = f.type === 'html' ? (f.label.replace(/\s+/g, ' ').trim() || 'Info text') : (f.label || 'Untitled field')
    return (
        <li className={cn(
            'flex items-center gap-1 rounded-lg border bg-card p-1',
            selected ? 'border-primary ring-1 ring-primary/40' : hasProblem ? 'border-destructive/50' : 'border-border',
        )}>
            <button
                type="button"
                aria-current={selected}
                onClick={onSelect}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 py-1.5 text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
            >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground" title={TYPE_LABEL[f.type]}>
                    <Icon className="size-4" aria-hidden />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{name}</span>
                    <span className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                        <code className="font-mono">{f.key}</code>
                        {f.required && <Badge variant="secondary">required</Badge>}
                        {f.hidden && <Badge variant="outline">hidden</Badge>}
                        {f.visibleWhen && <Badge variant="outline">conditional</Badge>}
                        {hasProblem && <Badge variant="destructive">needs attention</Badge>}
                    </span>
                </span>
            </button>
            <Button variant="ghost" size="icon-xs" aria-label={`Move ${name} up`} disabled={i === 0} onClick={() => onMove(-1)}><ArrowUp /></Button>
            <Button variant="ghost" size="icon-xs" aria-label={`Move ${name} down`} disabled={last} onClick={() => onMove(1)}><ArrowDown /></Button>
            <Button variant="ghost" size="icon-xs" aria-label={`Duplicate ${name}`} onClick={onDuplicate}><Copy /></Button>
            <Button variant="ghost" size="icon-xs" aria-label={`Delete ${name}`} onClick={onRemove}><Trash2 /></Button>
        </li>
    )
}

export default function FieldList({ page, selectedIndex, problemKeys, onSelect, onAdd, onDuplicate, onRemove, onMove }: Props) {
    const [confirm, setConfirm] = useState<number | null>(null)
    const target = confirm !== null ? page.fields[confirm] : undefined

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <h2 className="truncate text-sm font-semibold">{page.title || 'Untitled page'}</h2>
                <AddFieldMenu onAdd={onAdd} />
            </div>
            {page.fields.length === 0 ? (
                <Empty className="border border-dashed">
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><FileQuestion /></EmptyMedia>
                        <EmptyTitle>No fields on this page</EmptyTitle>
                        <EmptyDescription>Add the first question for this page.</EmptyDescription>
                    </EmptyHeader>
                    <AddFieldMenu onAdd={onAdd} label="Add the first field" />
                </Empty>
            ) : (
                <ul className="flex flex-col gap-2" aria-label={`Fields on ${page.title}`}>
                    {page.fields.map((f, i) => (
                        <FieldCard
                            key={i}
                            f={f} i={i} last={i === page.fields.length - 1}
                            selected={selectedIndex === i}
                            hasProblem={problemKeys.has(f.key)}
                            onSelect={() => onSelect(i)}
                            onDuplicate={() => onDuplicate(i)}
                            onRemove={() => setConfirm(i)}
                            onMove={(d) => onMove(i, d)}
                        />
                    ))}
                </ul>
            )}
            <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete {target?.label || target?.key || 'field'}?</DialogTitle>
                        <DialogDescription>
                            Conditions and rules that use this field will need fixing. Answers already collected under it are kept with their submissions.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={() => { if (confirm !== null) onRemove(confirm); setConfirm(null) }}>Delete field</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
